// Performance: the analyst books against DS70 from their Refinitiv reports (attribution
// DB). Periods = the deck's returns + risk table at the latest month with an SI report
// for every book; Monthly = each month's return; Financial Year and Custom chain the
// monthly returns; Risk = the SI reports' realised statistics. A window with a month
// whose MONTHLY report is not loaded shows no value and names the month.
import React, { useMemo, useState } from 'react';
import { API, NEG_FILL, POS_FILL, fmtDmy, fmtMonth, fmtNum, fmtPct, useApiGet } from './apUtils';
import { Notice, Toggle } from './apComponents';
import { DeckGaps, ReturnsRiskTable } from './DeckTables';

const p2 = (v) => fmtPct(v, 2);

// Chain monthly returns (percent) over `months`; null with the missing months if any is absent.
const chain = (byMonth, months) => {
  const missing = months.filter((m) => byMonth[m] == null);
  if (missing.length || !months.length) return { value: null, missing };
  const cum = months.reduce((acc, m) => acc * (1 + byMonth[m] / 100), 1);
  return { value: (cum - 1) * 100, missing: [] };
};
const fyOf = (iso) => { const [y, m] = iso.split('-').map(Number); return m >= 7 ? y + 1 : y; };

const RISK = [
  ['Standard Deviation', 'Vol (p.a.)', (v) => p2(v)],
  ['Realized Tracking Error', 'TE (p.a.)', (v) => (v ? p2(v * 100) : '-')],
  ['Realized Beta', 'Beta', (v) => fmtNum(v)],
  ['Information Ratio', 'Info ratio', (v) => fmtNum(v)],
  ['Sharpe Ratio', 'Sharpe', (v) => fmtNum(v)],
  ['Maximum Drawdown', 'Max drawdown', (v) => (v == null ? '–' : p2(-Math.abs(v) * 100))],
  ['Upside Capture Ratio', 'Upside capture', (v) => (v == null ? '–' : p2(v * 100))],
  ['Downside Capture Ratio', 'Downside capture', (v) => (v == null ? '–' : p2(v * 100))],
  ['Batting Average', 'Batting average', (v) => (v == null ? '–' : p2(v * 100))],
  ['Realized Alpha', 'Alpha', (v) => (v == null ? '–' : p2(v * 100))],
];

function vsDs70Fill(v, d) {
  if (v == null || d == null || v === d) return undefined;
  return { background: v > d ? POS_FILL : NEG_FILL };
}

export default function PerformanceTab({ books }) {
  const { data, error, loading } = useApiGet(`${API}/performance`);
  const [view, setView] = useState('periods');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  const rows = useMemo(() => [{ code: 'DRU007SU', name: 'DS70', bench: true },
    ...books.filter((b) => b.kind === 'analyst').map((b) => ({ code: b.code, name: b.name }))], [books]);

  const byMonth = useMemo(() => {
    const out = {};
    if (!data?.monthly) return out;
    Object.entries(data.monthly.returns).forEach(([code, vals]) => {
      out[code] = {};
      data.monthly.months.forEach((m, i) => { out[code][m] = vals[i]; });
    });
    return out;
  }, [data]);

  if (error) return <div className="tab-panel"><Notice kind="error">{error}</Notice></div>;
  if (loading) return <div className="tab-panel"><div className="chart-loading">Loading performance...</div></div>;
  if (!data?.available) {
    return (
      <div className="tab-panel">
        <Notice>
          No month has an SI report for every analyst book in the portal yet. Performance appears once the attribution
          DB, with the books&apos; monthly and SI reports, is synced to the cloud.
        </Notice>
      </div>
    );
  }

  const months = data.monthly.expected_months;
  const fys = [...new Set(months.map(fyOf))];
  const s = start && months.includes(start) ? start : months[0];
  const e = end && months.includes(end) ? end : months[months.length - 1];
  const customMonths = months.filter((m) => m >= s && m <= e);
  const annualise = customMonths.length > 12;

  return (
    <div className="tab-panel">
      <div className="chart-controls">
        <Toggle value={view} onChange={setView} options={[['periods', 'Periods'], ['monthly', 'Monthly'],
          ['fy', 'Financial Year'], ['risk', 'Risk'], ['custom', 'Custom Period']]} />
        <span className="ap-asat">As at {fmtDmy(data.latest)} · benchmark DS70 (Yarra Lane - DS70 Super)</span>
      </div>

      {view === 'periods' && (
        <>
          <ReturnsRiskTable deck={data.periods} />
          <DeckGaps deck={data.periods} />
        </>
      )}

      {view === 'monthly' && (
        <div className="table-container">
          <table className="ap-deck-table">
            <thead>
              <tr><th className="ap-deck-name">Portfolio</th>{months.map((m) => <th key={m}>{fmtMonth(m)}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.code} className={r.bench ? 'ap-deck-bench' : undefined}>
                  <td className="ap-deck-name">{r.name}</td>
                  {months.map((m) => {
                    const v = byMonth[r.code]?.[m];
                    return <td key={m} style={r.bench ? undefined : vsDs70Fill(v, byMonth.DRU007SU?.[m])}>{v == null ? '-' : p2(v)}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="ap-footnote">Each month&apos;s Total Period Return from its MONTHLY report (the first month is part of a
            month, from inception). &quot;-&quot; = that month&apos;s report is not in the attribution DB.</p>
        </div>
      )}

      {view === 'fy' && (
        <div className="table-container">
          <table className="ap-deck-table">
            <thead>
              <tr>
                <th className="ap-deck-name">Portfolio</th>
                {fys.map((fy) => {
                  const fm = months.filter((m) => fyOf(m) === fy);
                  const partial = fm.length < 12;
                  return <th key={fy}>FY{String(fy).slice(2)}{partial ? (fy === fys[fys.length - 1] ? ' YTD' : ' (part)') : ''}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.code} className={r.bench ? 'ap-deck-bench' : undefined}>
                  <td className="ap-deck-name">{r.name}</td>
                  {fys.map((fy) => {
                    const fm = months.filter((m) => fyOf(m) === fy);
                    const v = chain(byMonth[r.code] || {}, fm);
                    const d = chain(byMonth.DRU007SU || {}, fm);
                    return (
                      <td key={fy} style={r.bench ? undefined : vsDs70Fill(v.value, d.value)}
                        title={v.missing.length ? `Needs ${v.missing.map(fmtMonth).join(', ')}` : undefined}>
                        {v.value == null ? '-' : p2(v.value)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="ap-footnote">Financial years end 30 June; chained from the monthly reports, cumulative. A year
            missing any month&apos;s report shows &quot;-&quot; (hover for the months).</p>
        </div>
      )}

      {view === 'risk' && (
        <div className="table-container">
          <table className="ap-deck-table">
            <thead>
              <tr><th className="ap-deck-name">Portfolio</th>{RISK.map(([, label]) => <th key={label}>{label}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.code} className={r.bench ? 'ap-deck-bench' : undefined}>
                  <td className="ap-deck-name">{r.name}</td>
                  {RISK.map(([stat, label, f]) => {
                    if (r.bench && ['Realized Tracking Error', 'Information Ratio', 'Realized Alpha'].includes(stat)) {
                      return <td key={label}>-</td>;
                    }
                    return <td key={label}>{f(data.risk[r.code]?.[stat])}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="ap-footnote">Realised since inception ({fmtDmy(data.periods.inception)}) to {fmtDmy(data.latest)},
            from the SI reports&apos; key statistics, against DS70. With only a few months of history these are indicative.</p>
        </div>
      )}

      {view === 'custom' && (
        <>
          <div className="chart-controls">
            <div className="chart-portfolio-selector">
              <label htmlFor="ap-custom-start">From</label>
              <select id="ap-custom-start" value={s} onChange={(ev) => setStart(ev.target.value)}>
                {months.map((m) => <option key={m} value={m}>{fmtMonth(m)}</option>)}
              </select>
              <label htmlFor="ap-custom-end">to</label>
              <select id="ap-custom-end" value={e} onChange={(ev) => setEnd(ev.target.value)}>
                {months.filter((m) => m >= s).map((m) => <option key={m} value={m}>{fmtMonth(m)}</option>)}
              </select>
            </div>
          </div>
          <table className="ap-deck-table">
            <thead>
              <tr><th className="ap-deck-name">Portfolio</th><th>{annualise ? 'Return (p.a.)' : 'Return'}</th><th>DS70</th><th>Difference</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const v = chain(byMonth[r.code] || {}, customMonths);
                const d = chain(byMonth.DRU007SU || {}, customMonths);
                const ann = (x) => (x == null || !annualise ? x : ((1 + x / 100) ** (12 / customMonths.length) - 1) * 100);
                const va = ann(v.value);
                const da = ann(d.value);
                return (
                  <tr key={r.code} className={r.bench ? 'ap-deck-bench' : undefined}>
                    <td className="ap-deck-name">{r.name}</td>
                    <td style={r.bench ? undefined : vsDs70Fill(va, da)}
                      title={v.missing.length ? `Needs ${v.missing.map(fmtMonth).join(', ')}` : undefined}>
                      {va == null ? '-' : p2(va)}
                    </td>
                    <td>{da == null ? '-' : p2(da)}</td>
                    <td>{va == null || da == null ? '-' : fmtPct(va - da, 2, true)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="ap-footnote">{customMonths.length} months, {fmtMonth(s)} to {fmtMonth(e)}, chained from the monthly
            reports{annualise ? ' and annualised' : ' (cumulative)'}.</p>
        </>
      )}
    </div>
  );
}
